// PenStatusDetail — the three-layer connection breakdown surfaced from the
// PenStatusBadge popover. Issue 5: "include status when the iOS app is
// connected and when the pen is connected, etc."
//
// The old badge collapsed everything into one tone, so "disconnected" meant
// BOTH "browser↔server WebSocket is down" AND "WebSocket is up but no iOS
// publisher is forwarding data." Those are different failures with different
// fixes, so we split the status into three explicit, independently-derived
// layers — each rendered as its own dot + label here in the popover:
//
//   1. Server — browser ↔ server WebSocket, straight from `state`.
//   2. iOS app — the publisher forwarding pen data (see note below).
//   3. Pen — the BLE link between the iOS app and the physical pen.
//
// The badge itself stays compact (readings + a single summary dot for the
// worst layer); this detail panel lives behind the popover.

import { useCallback, useEffect, useRef, useState } from "react";

import type { useLiveStream } from "@/lib/livestream/use-livestream";
import { cn } from "@/lib/utils";

type LiveStream = ReturnType<typeof useLiveStream>;

// FRESH_WINDOW_MS — how long since the last sample before the PEN layer
// degrades from "Live" to "Idle". Pen sample rate can be configured down to
// ~1 Hz, so a short window flickers between states even on a healthy stream.
// 12s tolerates a missed sample or two without crying disconnect. (Mirrors the
// value the badge has historically used for its single tone.)
export const FRESH_WINDOW_MS = 12_000;

// APP_PRESENCE_WINDOW_MS — fallback freshness window for INFERRING iOS-app
// presence from frame activity, used only when the hub's authoritative
// publisher-presence signal (`publisherOnline`) isn't available — i.e. it
// hasn't reported yet, or the server is an older build. When present, that
// signal is preferred (see derivePenLayers). The inference treats "any
// pen_status or live_sample frame within this window, while the socket is
// open" as the app forwarding; it's wider than the pen freshness window
// because a dropped BLE pen still emits pen_status frames even when no
// live_sample arrives.
export const APP_PRESENCE_WINDOW_MS = 30_000;

/** Health levels, ordered worst → best. The badge's summary dot uses the
 *  worst level across all three layers (the lowest `rank`). */
export type LayerLevel = "down" | "pending" | "up";

const LEVEL_RANK: Record<LayerLevel, number> = {
  down: 0,
  pending: 1,
  up: 2,
};

export interface LayerStatus {
  level: LayerLevel;
  /** Short status word shown next to the dot, e.g. "Connected". */
  label: string;
  /** Optional secondary detail line (e.g. a pen disconnect reason). */
  detail?: string;
  /** Whether the pen layer should pulse its dot (fresh sample arriving). */
  pulse?: boolean;
}

export interface PenLayers {
  server: LayerStatus;
  app: LayerStatus;
  pen: LayerStatus;
}

interface DeriveArgs {
  state: LiveStream["state"];
  lastStatus: LiveStream["lastStatus"];
  lastSample: LiveStream["lastSample"];
  /** Authoritative hub publisher-presence; null until reported (or old server). */
  publisherOnline: boolean | null;
  now: number;
}

/**
 * derivePenLayers maps the raw live-stream snapshot onto the three connection
 * layers. Pure so the threshold math is testable without a socket.
 */
export function derivePenLayers(a: DeriveArgs): PenLayers {
  const serverOpen = a.state === "open";

  // --- Layer 1: Server (browser ↔ server WebSocket) ---
  let server: LayerStatus;
  if (serverOpen) {
    server = { level: "up", label: "Connected" };
  } else if (a.state === "connecting" || a.state === "reconnecting") {
    server = { level: "pending", label: "Connecting…" };
  } else {
    // "idle" (socket never opened) or "closed" (gave up / disconnected).
    server = { level: "down", label: "Offline" };
  }

  // --- Layer 2: iOS app (publisher) — INFERRED from frame activity ---
  // See APP_PRESENCE_WINDOW_MS: with no authoritative presence signal we infer
  // the app is forwarding when the server is open AND we've seen a status or
  // sample frame recently. Without the server socket open we can't see any
  // publisher traffic, so the app layer is unknowable → treat as down.
  const lastFrameAt = Math.max(
    a.lastSample?.receivedAt ?? 0,
    a.lastStatus?.receivedAt ?? 0,
  );
  const appFresh =
    serverOpen && lastFrameAt > 0 && a.now - lastFrameAt < APP_PRESENCE_WINDOW_MS;
  // Prefer the hub's authoritative publisher-presence signal; fall back to
  // frame-activity inference when the server hasn't reported it yet (or is an
  // older build). Fresh frames also imply an app — you can't stream without
  // one — so they count as online regardless of the presence signal.
  const appOnline = a.publisherOnline === true || appFresh;
  let app: LayerStatus;
  if (!serverOpen) {
    app = { level: "down", label: "Unknown", detail: "Server offline" };
  } else if (appOnline) {
    app = { level: "up", label: "Connected" };
  } else if (a.publisherOnline === false) {
    // Authoritative: the hub says no publisher is attached to this tenant.
    app = { level: "down", label: "No app connected" };
  } else {
    // No presence signal yet and no recent traffic — unknown so far.
    app = { level: "pending", label: "Waiting for app" };
  }

  // --- Layer 3: Pen (BLE link) ---
  const sampleFresh =
    a.lastSample !== null && a.now - a.lastSample.receivedAt < FRESH_WINDOW_MS;
  let pen: LayerStatus;
  if (!serverOpen) {
    // Can't observe the pen at all without the socket.
    pen = { level: "down", label: "Unknown" };
  } else if (sampleFresh) {
    // A recent sample is ground truth: data is flowing, the pen is live no
    // matter what lastStatus.connected claims (some publisher builds
    // underreport status while actively streaming).
    pen = { level: "up", label: "Live", pulse: true };
  } else if (!a.lastSample && !a.lastStatus) {
    // Nothing seen yet — no basis to call the pen idle vs. live.
    pen = { level: "pending", label: "Unknown" };
  } else {
    // Stale samples and/or an explicit connected:false status. Surface the
    // publisher-reported reason when present (e.g. "out of range").
    const reason = a.lastStatus?.status.reason;
    pen = {
      level: "pending",
      label: a.lastStatus?.status.connected === false ? "Disconnected" : "Idle",
      detail: reason ?? undefined,
    };
  }

  return { server, app, pen };
}

/** The worst layer determines the badge's single summary dot. */
export function worstLevel(layers: PenLayers): LayerLevel {
  const all = [layers.server, layers.app, layers.pen];
  return all.reduce<LayerLevel>(
    (worst, l) => (LEVEL_RANK[l.level] < LEVEL_RANK[worst] ? l.level : worst),
    "up",
  );
}

/** Headline — collapses the three layers into one concise status for the
 *  header chip: the FIRST broken link in the chain Server → iOS app → Pen,
 *  i.e. the thing the user needs to act on. When every layer is up the pen is
 *  live and we say so (and pulse). The popover still shows the full breakdown.
 *  Labels are phrased as a hint at what to do next (e.g. "No iOS app"). */
export function headlineStatus(layers: PenLayers): {
  label: string;
  level: LayerLevel;
  pulse: boolean;
} {
  if (layers.server.level !== "up") {
    // "Connecting…" or "Offline".
    return { label: layers.server.label, level: layers.server.level, pulse: false };
  }
  if (layers.app.level !== "up") {
    // Server is up but the iOS publisher isn't attached / not yet known.
    const label =
      layers.app.label === "No app connected" ? "No iOS app" : layers.app.label;
    return { label, level: layers.app.level, pulse: false };
  }
  if (layers.pen.level !== "up") {
    // App is attached but the pen isn't streaming.
    const label =
      layers.pen.label === "Idle"
        ? "Pen idle"
        : layers.pen.label === "Disconnected"
          ? "Pen disconnected"
          : "No pen"; // pen "Unknown" while the app is up == no pen reporting
    return { label, level: layers.pen.level, pulse: false };
  }
  return { label: "Live", level: "up", pulse: true };
}

export function levelDotClass(level: LayerLevel): string {
  switch (level) {
    case "up":
      return "bg-emerald-500";
    case "pending":
      return "bg-amber-500";
    case "down":
    default:
      return "bg-muted-foreground/40";
  }
}

export function levelTextClass(level: LayerLevel): string {
  switch (level) {
    case "up":
      return "text-emerald-700 dark:text-emerald-400";
    case "pending":
      return "text-amber-700 dark:text-amber-400";
    case "down":
    default:
      return "text-muted-foreground";
  }
}

/** One row in the breakdown: layer name, dot, status word, optional detail. */
function LayerRow({ name, status }: { name: string; status: LayerStatus }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span className="text-sm text-muted-foreground">{name}</span>
      <span className="flex flex-col items-end gap-0.5 text-right">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 text-sm font-medium",
            levelTextClass(status.level),
          )}
        >
          <span
            className={cn(
              "inline-block size-2 rounded-full",
              levelDotClass(status.level),
              status.pulse ? "animate-pulse" : "",
            )}
            aria-hidden
          />
          {status.label}
        </span>
        {status.detail ? (
          <span className="text-xs text-muted-foreground">{status.detail}</span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * PenStatusDetail renders the three-layer breakdown plus the reconnect action.
 * It owns its own per-second clock so the layers re-derive as freshness windows
 * elapse — the parent badge doesn't need to tick for the popover to stay live.
 */
export function PenStatusDetail({
  state,
  lastStatus,
  lastSample,
  publisherOnline,
  onReconnect,
}: {
  state: LiveStream["state"];
  lastStatus: LiveStream["lastStatus"];
  lastSample: LiveStream["lastSample"];
  publisherOnline: boolean | null;
  onReconnect: () => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const layers = derivePenLayers({
    state,
    lastStatus,
    lastSample,
    publisherOnline,
    now,
  });

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Connection status</h3>
      </div>
      <div className="divide-y divide-border">
        <LayerRow name="Server" status={layers.server} />
        <LayerRow name="iOS app" status={layers.app} />
        <LayerRow name="Pen" status={layers.pen} />
      </div>
      <div className="mt-2 flex justify-end border-t border-border pt-3">
        <PopoverReconnectButton
          onReconnect={onReconnect}
          disabled={state !== "open" || publisherOnline === false}
          disabledReason={
            state !== "open" ? "Server offline" : "No iOS app connected"
          }
        />
      </div>
    </div>
  );
}

/**
 * PopoverReconnectButton nudges the publisher to reconnect the pen and re-assert
 * our stream demand. Briefly flashes "sent" so the user sees the request went
 * out (the layers turn green on their own once frames resume). Disabled while
 * the server socket is down — the command can't route anywhere until it's open;
 * the socket auto-reconnects on its own ladder.
 */
function PopoverReconnectButton({
  onReconnect,
  disabled,
  disabledReason,
}: {
  onReconnect: () => void;
  disabled: boolean;
  disabledReason?: string;
}) {
  const [sent, setSent] = useState(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );
  const handle = useCallback(() => {
    if (disabled) return;
    onReconnect();
    setSent(true);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setSent(false), 1500);
  }, [onReconnect, disabled]);

  return (
    <button
      type="button"
      onClick={handle}
      disabled={disabled}
      title={disabled ? (disabledReason ?? "Unavailable") : "Reconnect pen"}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
        "hover:bg-accent/40 hover:text-foreground",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-40",
        sent ? "text-emerald-700 dark:text-emerald-400" : "",
      )}
    >
      <svg
        className={cn("size-3.5", sent ? "animate-spin" : "")}
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden
      >
        <path
          d="M13.5 8a5.5 5.5 0 11-1.6-3.9M13.5 2v3h-3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {sent ? "Sent" : "Reconnect"}
    </button>
  );
}
